import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, CheckCircle2, Phone, Mail, Instagram, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const CONTACT_PHONE = "917013095204";
const CONTACT_EMAIL = "abhishek.zindal@gmail.com";
const INSTAGRAM_URL = "https://www.instagram.com/thebandsumiran/";

const inputCls = "w-full bg-ink border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, event_type: "General Enquiry", city: "", event_date: "", package: "" });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err, "Could not send your message"));
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="contact-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <Link to="/" className="flex items-baseline gap-2" data-testid="contact-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="contact-back-home">Back to site</Link>
      </header>

      <main className="px-6 md:px-12 py-14 md:py-20 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div>
          <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Contact Us</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-bone leading-tight" data-testid="contact-heading">
            Talk to <em className="text-gold">the band</em>
          </h1>
          <p className="mt-5 text-bone/65 leading-relaxed max-w-md">
            Questions about tickets, private shows, or collaborations — reach us directly.
          </p>
          <div className="mt-10 space-y-3 max-w-sm">
            {CONTACT_PHONE ? (
              <>
                <a href={`tel:+${CONTACT_PHONE}`}
                  className="flex items-center gap-3 px-6 py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors"
                  data-testid="contact-call-btn">
                  <Phone size={18} /> Call +91 70130 95204
                </a>
                <a href={`https://wa.me/${CONTACT_PHONE}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-3 px-6 py-4 border border-saffron/50 text-saffron tracking-wide hover:bg-saffron hover:text-ink transition-colors"
                  data-testid="contact-whatsapp-btn">
                  <MessageCircle size={18} /> WhatsApp Us
                </a>
              </>
            ) : (
              <p className="text-xs text-ash border border-bone/10 p-4" data-testid="contact-phone-pending">
                Phone & WhatsApp line publishing soon — email or DM us meanwhile.
              </p>
            )}
            <a href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-3 px-6 py-4 border border-bone/20 text-bone tracking-wide hover:border-saffron hover:text-saffron transition-colors"
              data-testid="contact-email-btn">
              <Mail size={18} /> {CONTACT_EMAIL}
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 px-6 py-4 border border-bone/20 text-bone tracking-wide hover:border-saffron hover:text-saffron transition-colors"
              data-testid="contact-instagram-btn">
              <Instagram size={18} /> @thebandsumiran
            </a>
          </div>
        </div>

        <div>
          {done ? (
            <div className="border border-bone/15 bg-surface/60 p-10 text-center" data-testid="contact-success">
              <CheckCircle2 size={44} className="mx-auto text-saffron" strokeWidth={1.5} />
              <h2 className="mt-5 font-serif text-3xl text-bone">Message sent.</h2>
              <p className="mt-3 text-sm text-ash">We'll get back to you within a day.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-bone/15 bg-surface/60 p-7 md:p-9 space-y-4" data-testid="contact-form">
              <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Write to us</p>
              <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
              <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
              <textarea required rows={5} placeholder="Your message…" value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" />
              <button type="submit" disabled={sending}
                className="w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="contact-submit-btn">
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
